import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { ApiError, createApiClient } from '../../services/api/client'
import {
  operatoryPolicyPayloadSchema,
  operatoryPolicyResponseSchema,
  type OperatoryPolicyPayload,
  type OperatoryPolicyResponse,
} from './operatory-policy-contracts'

export type OperatoryPolicyRepository = {
  getPolicy: (tenantId: string) => Promise<OperatoryPolicyResponse>
  updatePolicy: (
    tenantId: string,
    payload: OperatoryPolicyPayload
  ) => Promise<OperatoryPolicyPayload>
}

type RepositoryOptions = {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}

function endpoint(tenantId: string) {
  return `/dashboard/clinics/${encodeURIComponent(tenantId)}/appointment-manager/operatories`
}

export function createOperatoryPolicyRepository({
  getUser,
  baseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation,
}: RepositoryOptions): OperatoryPolicyRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })

  return {
    getPolicy(tenantId) {
      return client.request(endpoint(tenantId), {
        method: 'GET',
        responseSchema: operatoryPolicyResponseSchema,
      })
    },
    updatePolicy(tenantId, payload) {
      return client.request(endpoint(tenantId), {
        method: 'PATCH',
        body: operatoryPolicyPayloadSchema.parse(payload),
        responseSchema: operatoryPolicyPayloadSchema,
      })
    },
  }
}

export function isOperatoryPermissionError(error: unknown) {
  return (
    error instanceof ApiError &&
    (error.status === 401 ||
      error.status === 403 ||
      error.code === 'unauthenticated')
  )
}
